import { apiService } from '../services/apiService';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

/**
 * Comprehensive logout utility that clears all user data and caches
 * This ensures complete logout across the entire application
 * 
 * Usage Examples:
 * 
 * 1. Simple logout with redirect:
 *    await logoutAndRedirect('/login');
 * 
 * 2. Logout without redirect:
 *    await performCompleteLogout();
 * 
 * 3. Emergency logout (for security issues):
 *    await performEmergencyLogout();
 * 
 * 4. In React components:
 *    import LogoutButton from 'components/ui/LogoutButton';
 *    <LogoutButton showConfirmation={true}>Logout</LogoutButton>
 * 
 * 5. In Redux:
 *    import { logoutUser } from 'store/slices/authSlice';
 *    dispatch(logoutUser());
 */
import Config from '../Config';

export const performCompleteLogout = async (showToast: boolean = true): Promise<void> => {
  // Extract token and user info before clearing local storage
  const token = Cookies.get('authToken') || localStorage.getItem('token') || localStorage.getItem('authToken');
  const user = localStorage.getItem('user');
  let userId: any = null;
  if (user) {
    try {
      const parsed = JSON.parse(user);
      userId = parsed?.id;
    } catch (e) {}
  }

  // Fire-and-forget backend session expiration & logout calls (DO NOT WAIT)
  if (token || userId) {
    try {
      apiService.post('/auth/expire-session', { token, userId }).catch(() => {});
      apiService.post('/auth/logout', { token, userId }).catch(() => {});

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
          const blob = new Blob([JSON.stringify({ token, userId })], { type: 'application/json' });
          const beaconUrl = `${Config.APIBASEURL || ''}/auth/expire-session`;
          navigator.sendBeacon(beaconUrl, blob);
        } catch (e) {}
      }
    } catch (e) {
      console.warn('Non-blocking logout notification failed:', e);
    }
  }

  // Clear API service cache and cancel requests
  apiService.clearAllCache();
  apiService.cancelAllRequests();

  // Clear all authentication cookies
  const cookiesToClear = [
    'authToken',
    'refreshToken', 
    'user',
    'session',
    'remember_token',
    'csrf_token',
    'xsrf_token',
    'access_token',
    'id_token'
  ];

  cookiesToClear.forEach(cookieName => {
    // Clear cookie for current domain and path
    Cookies.remove(cookieName);
    Cookies.remove(cookieName, { path: '/' });
    
    // Clear for current hostname
    if (window.location.hostname) {
      Cookies.remove(cookieName, { path: '/', domain: window.location.hostname });
      Cookies.remove(cookieName, { path: '/', domain: `.${window.location.hostname}` });
    }
  });

  // Clear localStorage except user preferences
  const keysToKeep = [
    'theme',
    'language',
    'cookieConsent',
    'userPreferences',
    'appSettings'
  ];

  const allKeys = Object.keys(localStorage);
  allKeys.forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  // Clear all sessionStorage
  sessionStorage.clear();

  // Clear IndexedDB if used
  try {
    if ('indexedDB' in window) {
      // Clear common IndexedDB databases
      const dbNames = ['userCache', 'appData', 'offlineData'];
      dbNames.forEach(dbName => {
        const deleteReq = indexedDB.deleteDatabase(dbName);
        deleteReq.onerror = () => console.warn(`Failed to delete IndexedDB: ${dbName}`);
      });
    }
  } catch (error) {
    console.warn('IndexedDB cleanup failed:', error);
  }

  // Clear browser cache storage if available
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    } catch (error) {
      console.warn('Could not clear cache storage:', error);
    }
  }

  // Clear any WebSQL databases (deprecated but still might exist)
  try {
    if ('openDatabase' in window) {
      // This is deprecated and not recommended, but clearing for completeness
    }
  } catch (error) {
    console.warn('WebSQL cleanup check failed:', error);
  }

  // Show success message
  if (showToast) {
    toast.success('Logged out successfully');
  }

  // Force a small delay before redirect to ensure cleanup completes
  await new Promise(resolve => setTimeout(resolve, 100));
};

/**
 * Emergency logout - clears everything and forces page reload
 * Use this when normal logout fails or for security purposes
 */
export const performEmergencyLogout = async (): Promise<void> => {
  try {
    await performCompleteLogout(false);
  } catch (error) {
    console.error('Emergency logout cleanup failed:', error);
  }

  // Force immediate page reload to clear any remaining state
  window.location.href = '/login';
};

/**
 * Check if user is properly logged out (no tokens or user data remaining)
 */
export const verifyLogoutComplete = (): boolean => {
  // Check for any remaining authentication tokens
  const cookieToken = Cookies.get('authToken');
  const localToken = localStorage.getItem('token');
  const sessionToken = sessionStorage.getItem('authToken');
  const userData = localStorage.getItem('user');

  return !cookieToken && !localToken && !sessionToken && !userData;
};

/**
 * Logout with redirect
 */
export const logoutAndRedirect = async (redirectPath: string = '/login'): Promise<void> => {
  await performCompleteLogout();
  
  // Use window.location for hard navigation to ensure clean state
  window.location.href = redirectPath;
};